/**
 * OnboardingEditorActionsV2 Component
 *
 * V2-only editor toolbar actions for StepNode-based onboarding editing.
 * Does not import V1 ScreenGraph services, V1 locale remappers, or V1 theme IO.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';
import { WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { GUIDE_ANCHORS } from '@modules/guides/anchors/guideAnchorRegistry';
import { stripQueryParam } from '@modules/guides/hooks/guideHighlight';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PublicIcon from '@mui/icons-material/Public';
import PaletteIcon from '@mui/icons-material/Palette';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import TranslateIcon from '@mui/icons-material/Translate';
import LinkIcon from '@mui/icons-material/Link';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import type { UseOnboardingEditorActionsV2Return } from '../hooks/useOnboardingEditorActionsV2';
import { useOnboardingThemeSettingsV2 } from '../hooks/useOnboardingThemeSettingsV2';
import {
  buildThemeV2ExportData,
  downloadThemeV2JSON,
  parseThemeV2ImportFile,
} from '../services/themeSettingsIOV2';
import { UnifiedWebPlacementFlow } from '@modules/webplacements/components';
import { PostPublishVersionDialog } from './PostPublishVersionDialog';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { StrategyDivergenceChip } from '@modules/strategy-blocks/components/StrategyDivergenceChip';
import { StrategyStalenessBadge } from '@modules/strategy-blocks/components/StrategyStalenessBadge';
import { StrategyStalenessService, type StalenessStrategyInput } from '@modules/strategy-blocks/services/StrategyStalenessService';
import { InsightImpactDialog } from '@modules/strategy-blocks/components/InsightImpactDialog';
import type { CliStrategyStaleness, StrategyInputManifest } from '@modules/shared-types';
import type { StrategyStatus } from '@segmently/schema-models/strategy';
import { useAuth } from '@frontend/contexts/AuthContext';
import { ONBOARDING_EDITOR_TEST_IDS } from '@modules/onboardings/testIds';
import { DEPLOY_ACTION_PARAM, buildDeployActionKey, isDeployActionRequested } from '../utils/deployActionParam';

export interface OnboardingEditorActionsV2Props {
  actions: UseOnboardingEditorActionsV2Return;
  projectId: string;
  onboardingId: string;
  onboardingName: string;
  associatedStrategyId?: string | null;
  onAssociateStrategy?: () => void;
  onOpenAssociatedStrategy?: () => void;
  /**
   * Opens the theme picker that replaces this onboarding's theme (the canvas hub owns it).
   * Admin only for now: the item renders only for a confirmed admin.
   */
  onChangeTheme?: () => void;
}

export const OnboardingEditorActionsV2: React.FC<OnboardingEditorActionsV2Props> = ({
  actions,
  projectId,
  onboardingId,
  onboardingName,
  associatedStrategyId,
  onAssociateStrategy,
  onOpenAssociatedStrategy,
  onChangeTheme,
}) => {
  const { currentUser, isAdmin, adminLoaded } = useAuth();
  const canChangeTheme = Boolean(onChangeTheme) && isAdmin && adminLoaded;
  const location = useLocation();
  const navigate = useNavigate();
  const guideService = React.useMemo(() => new GuideService(), []);
  // AC-012 / DC-18: guards the `?action=deploy` read-once effect below against
  // StrictMode's double-invoke, so the deploy flow never opens twice. Scoped to
  // `${onboardingId}|${search}` (fix round 1), NOT a plain boolean — this editor
  // instance can survive a route-param navigation to a different onboardingId
  // without remounting, and a boolean would then silently swallow that new
  // onboarding's own legitimate `?action=deploy` intent.
  const deployActionConsumedKeyRef = useRef<string | null>(null);
  const {
    versions,
    selectedVersion,
    selectedVersionId,
    switchVersion,
    createVersion,
    markVersionFrozen,
    exportJSON,
    importJSON,
    loading,
    error,
    clearError,
    successMessage,
    clearSuccess,
    warningMessage,
    clearWarning,
    exportLocales,
    importLocales,
  } = actions;

  const [versionMenuAnchor, setVersionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  const [createVersionDialogOpen, setCreateVersionDialogOpen] = useState(false);
  const [newVersionName, setNewVersionName] = useState('');
  // U8 (Spec D2): operator note on what changed, stored on the version doc.
  const [newVersionDescription, setNewVersionDescription] = useState('');
  const [isCreatingVersion, setIsCreatingVersion] = useState(false);
  const [deployWebFlowOpen, setDeployWebFlowOpen] = useState(false);
  const [postPublishVersionOpen, setPostPublishVersionOpen] = useState(false);

  // AC-012 / DC-18: `?action=deploy` opens the flow exactly once, then is stripped from
  // the LIVE URL (window.location) so it composes with useGuideHighlight's own strip in
  // the same commit; the highlight alone (`?guide=deploy-web`) never opens the flow.
  // The flow needs `selectedVersionId` (passed as `source.versionId` below) — if it isn't
  // ready yet on the first render, the effect re-checks on every `selectedVersionId`
  // change instead of dropping the intent, and only then consumes the once-guard.
  useEffect(() => {
    if (!isDeployActionRequested(location.search)) return;
    const key = buildDeployActionKey(onboardingId, location.search);
    if (deployActionConsumedKeyRef.current === key) return;
    if (!selectedVersionId) return;
    deployActionConsumedKeyRef.current = key;
    handleDeployWebDirect();
    navigate(
      {
        pathname: location.pathname,
        search: stripQueryParam(window.location.search, DEPLOY_ACTION_PARAM),
        hash: location.hash,
      },
      { replace: true },
    );
  }, [location.search, location.pathname, location.hash, navigate, selectedVersionId, onboardingId]);

  // Two-axis divergence signal (Spec A / D51, A5-2): compare the linked strategy's
  // lifecycle against this version's compile provenance. One doc read per strategy.
  const [linkedStrategyLifecycle, setLinkedStrategyLifecycle] = useState<{
    status?: StrategyStatus;
    currentVersion?: string;
    name?: string;
  } | null>(null);
  // D3 (AC-D3-6): staleness of the linked strategy's pinned inputs, shown in
  // THIS header so the funnel side sees "built on vN — vM available" too.
  const [strategyStaleness, setStrategyStaleness] = useState<CliStrategyStaleness | null>(null);
  const [linkedStrategyManifest, setLinkedStrategyManifest] = useState<StrategyInputManifest | undefined>(undefined);
  const [whatChangedOpen, setWhatChangedOpen] = useState(false);
  useEffect(() => {
    if (!associatedStrategyId) {
      setLinkedStrategyLifecycle(null);
      setStrategyStaleness(null);
      setLinkedStrategyManifest(undefined);
      return;
    }
    let active = true;
    new StrategyService(projectId)
      .get(associatedStrategyId)
      .then(async (strategy) => {
        if (!active) return;
        const doc = strategy as ({ status?: StrategyStatus; currentVersion?: string; name?: string } & StalenessStrategyInput) | null;
        setLinkedStrategyLifecycle(doc ? { status: doc.status, currentVersion: doc.currentVersion, name: doc.name } : null);
        setLinkedStrategyManifest(doc?.inputManifest);
        if (doc?.inputManifest) {
          const verdict = await new StrategyStalenessService(projectId).compute(doc).catch(() => null);
          if (active) setStrategyStaleness(verdict);
        } else {
          setStrategyStaleness(null);
        }
      })
      .catch(() => {
        if (active) {
          setLinkedStrategyLifecycle(null);
          setStrategyStaleness(null);
          setLinkedStrategyManifest(undefined);
        }
      });
    return () => {
      active = false;
    };
  }, [associatedStrategyId, projectId]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const localeFileInputRef = useRef<HTMLInputElement>(null);
  const themeFileInputRef = useRef<HTMLInputElement>(null);

  const {
    themeSettings,
    loading: themeLoading,
    refresh: refreshThemeSettings,
    importSettings: importThemeSettings,
  } = useOnboardingThemeSettingsV2({
    projectId,
    onboardingId,
  });

  const [themeNotification, setThemeNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const handleVersionClick = (event: React.MouseEvent<HTMLElement>) => {
    setVersionMenuAnchor(event.currentTarget);
  };

  const handleVersionMenuClose = () => {
    setVersionMenuAnchor(null);
  };

  const handleVersionSelect = (versionId: string) => {
    switchVersion(versionId);
    handleVersionMenuClose();
  };

  const handleCreateVersionClick = () => {
    handleVersionMenuClose();
    setNewVersionName(`v${versions.length + 1}`);
    setNewVersionDescription('');
    setCreateVersionDialogOpen(true);
  };

  const handleCreateVersionCancel = () => {
    setCreateVersionDialogOpen(false);
    setNewVersionName('');
    setNewVersionDescription('');
  };

  const handleCreateVersionSubmit = async () => {
    if (!newVersionName.trim()) return;

    setIsCreatingVersion(true);
    try {
      await createVersion(newVersionName.trim(), { description: newVersionDescription.trim() || undefined });
      setCreateVersionDialogOpen(false);
      setNewVersionName('');
      setNewVersionDescription('');
    } finally {
      setIsCreatingVersion(false);
    }
  };

  const handleActionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleDeployWebDirect = () => {
    setDeployWebFlowOpen(true);
  };

  // Publishing freezes the shipped version (Spec A / D51, A4-1/A4-3). The in-app
  // deploy path doesn't freeze server-side (that lives on the CLI publish path),
  // so freeze client-side the moment the deploy succeeds. The "create a new
  // version?" prompt is deferred to the deploy flow's CLOSE — it must not stack
  // on top of the "Published Successfully!" screen (GAP-2).
  const publishSucceededRef = useRef(false);
  const handlePublished = useCallback(async () => {
    publishSucceededRef.current = true;
    if (selectedVersionId) {
      try {
        await markVersionFrozen(selectedVersionId);
      } catch (err) {
        // Non-fatal: freezing is a hygiene marker, not part of the publish contract.
        console.warn('[OnboardingEditorActionsV2] Failed to mark version frozen on publish:', err);
      }
    }
    // Two-axis parity (GAP-3a): a BAKED linked strategy becomes `published`,
    // mirroring the CLI freezeOnPublish. A draft is never auto-locked.
    if (associatedStrategyId) {
      try {
        const strategyService = new StrategyService(projectId);
        const strategy = await strategyService.get(associatedStrategyId);
        if ((strategy as { status?: string } | null)?.status === 'baked') {
          await strategyService.update(associatedStrategyId, { status: 'published' } as never);
        }
      } catch (err) {
        console.warn('[OnboardingEditorActionsV2] Failed to flip linked strategy to published:', err);
      }
    }
  }, [selectedVersionId, markVersionFrozen, associatedStrategyId, projectId]);

  const handleDeployFlowClose = useCallback(() => {
    setDeployWebFlowOpen(false);
    if (publishSucceededRef.current) {
      publishSucceededRef.current = false;
      setPostPublishVersionOpen(true);
    }
  }, []);

  // Branch a new version so the just-published (frozen) one stays unchanged.
  const handleCreateNewVersionForPublish = useCallback(async () => {
    await createVersion(`v${versions.length + 1}`);
  }, [createVersion, versions.length]);

  const handleExport = async () => {
    handleActionsMenuClose();
    await exportJSON();
  };

  const handleImportClick = () => {
    handleActionsMenuClose();
    setTimeout(() => fileInputRef.current?.click(), 100);
  };

  const handleExportLocalesXlsx = async () => {
    handleActionsMenuClose();
    await exportLocales('xlsx');
  };

  const handleExportLocalesJson = async () => {
    handleActionsMenuClose();
    await exportLocales('json');
  };

  const handleImportLocalesClick = () => {
    handleActionsMenuClose();
    setTimeout(() => localeFileInputRef.current?.click(), 100);
  };

  const handleAssociateStrategy = useCallback(() => {
    handleActionsMenuClose();
    onAssociateStrategy?.();
  }, [onAssociateStrategy]);

  const handleOpenAssociatedStrategy = useCallback(() => {
    handleActionsMenuClose();
    onOpenAssociatedStrategy?.();
  }, [onOpenAssociatedStrategy]);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      event.target.value = '';
      await importJSON(file);
      window.location.reload();
    },
    [importJSON],
  );

  const handleLocaleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      event.target.value = '';
      await importLocales(file);
      window.location.reload();
    },
    [importLocales],
  );

  const handleExportTheme = useCallback(async () => {
    handleActionsMenuClose();
    // Re-read at the action boundary: the theme may have been changed or edited through another hook
    // instance (the canvas theme switch, the template panel's editor) since this menu loaded it.
    // No fallback to the copy captured at mount: a failed re-read must not export a stale theme.
    const settings = await refreshThemeSettings();
    if (!settings) {
      setThemeNotification({ open: true, message: 'Could not read the V2 theme settings to export', severity: 'error' });
      return;
    }

    try {
      const envelope = buildThemeV2ExportData(settings, projectId, onboardingId);
      downloadThemeV2JSON(envelope, onboardingName);
      setThemeNotification({ open: true, message: 'V2 theme exported successfully', severity: 'success' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to export V2 theme';
      setThemeNotification({ open: true, message, severity: 'error' });
    }
  }, [refreshThemeSettings, projectId, onboardingId, onboardingName]);

  const handleChangeThemeClick = useCallback(() => {
    handleActionsMenuClose();
    onChangeTheme?.();
  }, [onChangeTheme]);

  const handleImportThemeClick = useCallback(() => {
    handleActionsMenuClose();
    setTimeout(() => themeFileInputRef.current?.click(), 100);
  }, []);

  const handleThemeFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      event.target.value = '';

      try {
        const content = await file.text();
        const importedSettings = parseThemeV2ImportFile(content);
        await importThemeSettings(importedSettings);
        window.location.reload();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to import V2 theme';
        setThemeNotification({ open: true, message, severity: 'error' });
      }
    },
    [importThemeSettings],
  );

  return (
    <>
      {/* Spec D4 (U7): the association is a visible, clickable fact — same
          action as the menu's Open Strategy item. */}
      {associatedStrategyId && (
        <Tooltip title="Open the linked strategy canvas" arrow>
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            clickable={Boolean(onOpenAssociatedStrategy)}
            icon={<LinkIcon />}
            label={`Linked to ${linkedStrategyLifecycle?.name ?? 'strategy'}`}
            data-testid="onboarding-linked-strategy-chip"
            onClick={onOpenAssociatedStrategy}
          />
        </Tooltip>
      )}
      <StrategyDivergenceChip
        side="onboarding"
        linked={Boolean(associatedStrategyId)}
        strategyStatus={linkedStrategyLifecycle?.status}
        strategyCurrentVersion={linkedStrategyLifecycle?.currentVersion}
        compiledFromVersion={selectedVersion?.sourceStrategyVersion}
      />

      {/* D3 (AC-D3-6): linked strategy built on drifted inputs — [What changed]
          opens the impact tree scoped to pinned → latest. No fork from here. */}
      <StrategyStalenessBadge
        verdict={strategyStaleness}
        onWhatChanged={() => setWhatChangedOpen(true)}
      />

      <WithGuideHelp
        guideKey={GUIDE_ANCHORS.deployWeb.guideKey}
        guideService={guideService}
        data-testid={GUIDE_ANCHORS.deployWeb.testId}
        position="inline-end"
        iconComponent="span"
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<PublicIcon />}
          onClick={handleDeployWebDirect}
          disabled={loading}
          data-testid={ONBOARDING_EDITOR_TEST_IDS.deployWebBtn}
          sx={{
            minWidth: 'auto',
            px: 1.5,
            py: 0.5,
            fontSize: '0.8125rem',
            textTransform: 'none',
          }}
        >
          Deploy Web
        </Button>
      </WithGuideHelp>

      <Button
        variant="outlined"
        size="small"
        endIcon={<ExpandMoreIcon />}
        onClick={handleVersionClick}
        disabled={loading}
        data-testid="onboarding-version-dropdown-btn"
        sx={{
          minWidth: 'auto',
          maxWidth: 120,
          px: 1.5,
          py: 0.5,
          fontSize: '0.8125rem',
          textTransform: 'none',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          justifyContent: 'space-between',
        }}
      >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedVersion?.name || 'Version'}
        </Box>
      </Button>

      <Menu
        anchorEl={versionMenuAnchor}
        open={Boolean(versionMenuAnchor)}
        onClose={handleVersionMenuClose}
        data-testid="onboarding-version-menu"
      >
        <MenuItem onClick={handleCreateVersionClick} sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          <AddIcon sx={{ mr: 1, fontSize: 18 }} />
          Create New Version
        </MenuItem>
        {versions.length > 0 && <Divider />}
        {versions.map((v) => (
          <MenuItem
            key={v.id}
            onClick={() => handleVersionSelect(v.id)}
            selected={v.id === selectedVersionId}
            data-testid={`onboarding-version-item-${v.id}`}
          >
            {/* U8: name + operator description second line when present. */}
            <Box>
              <Box component="span">{v.name} ({new Date(v.createdAt).toLocaleDateString()})</Box>
              {v.description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 260 }} noWrap>
                  {v.description}
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <Button
        variant="outlined"
        size="small"
        endIcon={<ExpandMoreIcon />}
        onClick={handleActionsClick}
        disabled={loading}
        sx={{ minWidth: 'auto', px: 1.5, py: 0.5, fontSize: '0.8125rem' }}
      >
        Actions
      </Button>

      <Menu anchorEl={actionsMenuAnchor} open={Boolean(actionsMenuAnchor)} onClose={handleActionsMenuClose}>
        {/* U9 (Spec D4): the strategy link leads the menu with intent vocabulary. */}
        {(onAssociateStrategy || onOpenAssociatedStrategy) && (
          <ListSubheader disableSticky sx={{ lineHeight: '28px' }}>
            {associatedStrategyId ? 'Strategy — linked' : 'Strategy'}
          </ListSubheader>
        )}
        {onOpenAssociatedStrategy && associatedStrategyId && (
          <MenuItem
            onClick={handleOpenAssociatedStrategy}
            data-testid="onboarding-actions-open-associated-strategy"
          >
            <OpenInFullIcon sx={{ mr: 1, fontSize: 20 }} />
            Open Strategy
          </MenuItem>
        )}
        {onAssociateStrategy && (
          <MenuItem onClick={handleAssociateStrategy} data-testid="onboarding-actions-associate-strategy">
            <AccountTreeIcon sx={{ mr: 1, fontSize: 20 }} />
            {associatedStrategyId ? 'Re-associate Strategy…' : 'Associate Strategy…'}
          </MenuItem>
        )}
        {(onAssociateStrategy || onOpenAssociatedStrategy) && <Divider />}
        <MenuItem onClick={handleExport}>
          <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
          Export V2 JSON
        </MenuItem>
        <MenuItem onClick={handleImportClick}>
          <UploadFileIcon sx={{ mr: 1, fontSize: 20 }} />
          Import V2 JSON
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleExportLocalesXlsx} data-testid="onboarding-actions-export-locales-xlsx">
          <TranslateIcon sx={{ mr: 1, fontSize: 20 }} />
          Export Locales XLSX
        </MenuItem>
        <MenuItem onClick={handleExportLocalesJson} data-testid="onboarding-actions-export-locales-json">
          <TranslateIcon sx={{ mr: 1, fontSize: 20 }} />
          Export Locales JSON
        </MenuItem>
        <MenuItem onClick={handleImportLocalesClick} data-testid="onboarding-actions-import-locales-xlsx">
          <UploadFileIcon sx={{ mr: 1, fontSize: 20 }} />
          Import Locales XLSX
        </MenuItem>
        <Divider />
        <ListSubheader disableSticky sx={{ lineHeight: '28px' }}>
          Theme
        </ListSubheader>
        {canChangeTheme && (
          <MenuItem onClick={handleChangeThemeClick} data-testid={ONBOARDING_EDITOR_TEST_IDS.changeThemeMenuItem}>
            <SwapHorizIcon sx={{ mr: 1, fontSize: 20 }} />
            Change theme…
          </MenuItem>
        )}
        <MenuItem onClick={handleExportTheme} disabled={!themeSettings || themeLoading}>
          <PaletteIcon sx={{ mr: 1, fontSize: 20 }} />
          Export V2 Theme JSON
        </MenuItem>
        <MenuItem onClick={handleImportThemeClick}>
          <PaletteIcon sx={{ mr: 1, fontSize: 20 }} />
          Import V2 Theme JSON
        </MenuItem>
      </Menu>

      <input ref={fileInputRef} type="file" hidden accept=".json" onChange={handleFileChange} />
      <input ref={localeFileInputRef} type="file" hidden accept=".xlsx" onChange={handleLocaleFileChange} />
      <input ref={themeFileInputRef} type="file" hidden accept=".json" onChange={handleThemeFileChange} />

      {loading && <CircularProgress size={20} />}

      <SDialog
        open={createVersionDialogOpen}
        onClose={handleCreateVersionCancel}
        maxWidth="xs"
        fullWidth
        title="Create New Version"
        primaryAction={{
          label: 'Create',
          onClick: handleCreateVersionSubmit,
          disabled: isCreatingVersion || !newVersionName.trim(),
        }}
      >
        <Box sx={{ px: 3 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Version Name"
            fullWidth
            value={newVersionName}
            onChange={(event) => setNewVersionName(event.target.value)}
            disabled={isCreatingVersion}
            placeholder="e.g., v2, Draft 2026-05"
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            value={newVersionDescription}
            onChange={(event) => setNewVersionDescription(event.target.value)}
            disabled={isCreatingVersion}
            placeholder="What changed in this version?"
            inputProps={{ 'data-testid': 'onboarding-version-description' }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This creates a new V2 version based on the current StepNode version.
          </Typography>
        </Box>
      </SDialog>

      {/* D3 (AC-D3-6): impact tree for the linked strategy, opened from the
          staleness badge's [What changed]. */}
      {associatedStrategyId && (
        <InsightImpactDialog
          open={whatChangedOpen}
          onClose={() => setWhatChangedOpen(false)}
          projectId={projectId}
          strategyId={associatedStrategyId}
          defaultFromAnalysisId={linkedStrategyManifest?.insightsAnalysisId}
        />
      )}

      <UnifiedWebPlacementFlow
        open={deployWebFlowOpen}
        onClose={handleDeployFlowClose}
        projectId={projectId}
        userId={currentUser?.email || 'unknown'}
        source={{
          onboardingId,
          versionId: selectedVersionId,
          onboardingName,
          versionName: selectedVersion?.name,
        }}
        onPublished={handlePublished}
        onNavigate={navigate}
      />

      <PostPublishVersionDialog
        open={postPublishVersionOpen}
        onClose={() => setPostPublishVersionOpen(false)}
        publishedVersionName={selectedVersion?.name}
        onCreateNewVersion={handleCreateNewVersionForPublish}
        projectId={projectId}
        strategyId={associatedStrategyId}
      />

      <Snackbar open={!!error} autoHideDuration={6000} onClose={clearError} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={clearError}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar open={!!successMessage} autoHideDuration={4000} onClose={clearSuccess} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={clearSuccess}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar open={!!warningMessage} autoHideDuration={8000} onClose={clearWarning} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="warning" onClose={clearWarning}>
          {warningMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={themeNotification.open}
        autoHideDuration={4000}
        onClose={() => setThemeNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={themeNotification.severity}
          onClose={() => setThemeNotification(prev => ({ ...prev, open: false }))}
        >
          {themeNotification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OnboardingEditorActionsV2;
